// packages/kb-view/src/wiki-menu.ts
//
// LAT-1605 (LAT-1600 S5) — the nav-manifest resolver, moved out of apps/web so
// the in-app wiki rail and the public docs worker read the SAME `nav.yaml`
// grammar.
//
// The one thing that could not travel is the exclusion policy: apps/web hides
// the reserved `_*` namespaces (its mirror of the server policy) plus the
// manifest's own `wiki/nav.yaml`; the docs worker hides `help/nav.yaml` and
// underscore paths. So the policy is INJECTED — `createWikiMenuResolver` binds
// one `isExcludedPath` predicate for a surface and hands back the two
// resolvers. Binding once per surface (rather than threading a predicate
// through every call) is deliberate: the exclusion rule is a property of the
// surface, never of an individual lookup.
//
// The resolvers are generic over the caller's file row: they only ever read
// `path`/`filename`/`title` (KbViewFile) and hand the caller's own objects back
// in the menu, so an application row keeps every field the package cannot see.

import { isAlias, isMap, parseDocument, visit } from "yaml";

// LAT-1606 (LAT-1600 S6) — explicit `.js` because this module is now reachable
// through its own entry point (`@atmina/kb-view/wiki-menu`) from the content-law
// gate in `tools/scripts`, whose program is NodeNext. Extensionless relative
// specifiers are an error there; the package's own Bundler resolution and
// Vitest both map `.js` back to this `.ts` file, so nothing else changes.
import type { KbViewFile } from "./kb-file.js";

export type WikiMenuItem<File extends KbViewFile = KbViewFile> = {
  file: File;
  label: string;
};

export type WikiMenuSection<File extends KbViewFile = KbViewFile> = {
  label: string;
  items: WikiMenuItem<File>[];
  /** Schema 2 sub-groups. Absent — never empty — when the section is flat. */
  sections?: WikiMenuSection<File>[];
};

/** Decides which catalog paths a surface refuses to put in its menu. */
export type IsExcludedPath = (path: string) => boolean;

export interface WikiMenuResolver {
  /**
   * Resolve an authored `nav.yaml` manifest against the KB's catalog rows.
   * Returns null for an absent, malformed, or fully unresolvable manifest, so
   * a caller can fall back to `deriveWikiSections`.
   */
  resolveWikiMenu<File extends KbViewFile>(
    rows: File[],
    navSource: string | null,
  ): WikiMenuSection<File>[] | null;
  /** The folder-shaped fallback menu derived from the catalog alone. */
  deriveWikiSections<File extends KbViewFile>(
    rows: File[],
  ): WikiMenuSection<File>[];
}

const WIKI_SECTIONS = [
  ["getting-started", "Getting started"],
  ["concepts", "Concepts"],
  ["architecture", "Architecture"],
  ["guides", "Guides"],
  ["decisions", "Decisions"],
  ["reference", "Reference"],
] as const;

const KNOWN_SECTION_PREFIXES: ReadonlySet<string> = new Set(
  WIKI_SECTIONS.map(([prefix]) => prefix),
);

/**
 * LAT-1905 — folders the derived menu already places by hand. `meta/` carries
 * the Coverage map section below; it is a convention folder, not a general
 * neighborhood, so the folder pass must not claim it as well.
 */
const DERIVED_SPECIAL_FOLDERS: ReadonlySet<string> = new Set(["meta"]);

/**
 * The section label for a wiki folder, mirroring `folderLabel` in
 * `apps/mcp/src/core/nav/nav-plan.ts` — separators become spaces and only the
 * first character is raised. That module DEFINES the label a generated
 * `nav.yaml` section carries; this is its reader, duplicated rather than
 * imported because the hexagonal boundary forbids this package reaching into
 * the worker. The two must not disagree: a folder that generates as
 * "Open questions" has to derive as "Open questions" too, or the menu appears
 * to rename itself when a manifest lands.
 */
function derivedFolderLabel(folder: string): string {
  const words = folder.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  if (words.length === 0) return "";
  const [first, ...rest] = [...words];
  return `${first!.toUpperCase()}${rest.join("")}`;
}

function relativeWikiPath(path: string): string | null {
  const marker = "/wiki/";
  const index = path.indexOf(marker);
  if (index >= 0) return path.slice(index + marker.length);
  if (path.startsWith("wiki/")) return path.slice("wiki/".length);
  return null;
}

export function fileLabel(file: KbViewFile): string {
  return file.title?.trim() || file.filename.replace(/\.md$/i, "");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function trimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function resolvePages<File extends KbViewFile>(
  pages: unknown[],
  rowsByPath: Map<string, File>,
  isExcludedPath: IsExcludedPath,
): WikiMenuItem<File>[] {
  const seenPaths = new Set<string>();

  return pages.flatMap((page) => {
    const path = typeof page === "string"
      ? page
      : isRecord(page)
        ? page.path
        : null;
    if (typeof path !== "string" || path.length === 0) return [];
    if (isExcludedPath(path)) {
      return [];
    }

    let override: string | null = null;
    if (isRecord(page) && Object.hasOwn(page, "label")) {
      override = trimmedString(page.label);
      if (override === null) return [];
    }

    const file = rowsByPath.get(path);
    if (!file) return [];
    if (seenPaths.has(path)) return [];
    seenPaths.add(path);

    return [{ file, label: override ?? fileLabel(file) }];
  });
}

/**
 * Resolves one authored section. `requirePages` keeps schema 1 strict — there,
 * every section must carry a `pages` array. `nestingBudget` is the number of
 * further levels of sub-groups allowed; at zero a `sections` key is ignored,
 * which is what makes schema 1 flat and caps schema 2 at one level without
 * discarding the offending group's own pages.
 */
function resolveSection<File extends KbViewFile>(
  section: unknown,
  rowsByPath: Map<string, File>,
  isExcludedPath: IsExcludedPath,
  { requirePages, nestingBudget }: {
    requirePages: boolean;
    nestingBudget: number;
  },
): WikiMenuSection<File>[] {
  if (!isRecord(section)) return [];

  const pages = section.pages;
  const pagesMalformed = requirePages
    ? !Array.isArray(pages)
    : Object.hasOwn(section, "pages") && !Array.isArray(pages);
  if (pagesMalformed) return [];

  const label = trimmedString(section.label);
  if (label === null) return [];

  const items = Array.isArray(pages)
    ? resolvePages(pages, rowsByPath, isExcludedPath)
    : [];
  const nested = nestingBudget > 0 && Array.isArray(section.sections)
    ? section.sections.flatMap((child) =>
      resolveSection(child, rowsByPath, isExcludedPath, {
        requirePages: false,
        nestingBudget: nestingBudget - 1,
      })
    )
    : [];

  if (items.length === 0 && nested.length === 0) return [];
  return [nested.length > 0 ? { label, items, sections: nested } : { label, items }];
}

/**
 * Bind one surface's exclusion policy and get its nav resolvers.
 *
 * `isExcludedPath` is asked about every catalog path the menu would otherwise
 * show — both the authored-manifest path and the derived-folder path — so a
 * surface's hidden namespaces stay hidden however the menu is built.
 */
export function createWikiMenuResolver(
  { isExcludedPath }: { isExcludedPath: IsExcludedPath },
): WikiMenuResolver {
  return {
    resolveWikiMenu<File extends KbViewFile>(
      rows: File[],
      navSource: string | null,
    ): WikiMenuSection<File>[] | null {
      if (navSource === null) return null;

      try {
        const document = parseDocument(navSource, {
          merge: false,
          prettyErrors: false,
          uniqueKeys: true,
        });
        if (document.errors.length > 0 || !isMap(document.contents)) return null;

        let hasForbiddenReference = false;
        visit(document, {
          Node(_key, node) {
            if (isAlias(node) || node.anchor !== undefined) {
              hasForbiddenReference = true;
              return visit.BREAK;
            }
          },
        });
        if (hasForbiddenReference) return null;

        const value: unknown = document.toJS({ maxAliasCount: 0 });
        if (
          !isRecord(value) ||
          (value.schema !== 1 && value.schema !== 2) ||
          !Array.isArray(value.sections)
        ) {
          return null;
        }

        const rowsByPath = new Map(rows.map((file) => [file.path, file]));
        const sections = value.sections.flatMap((section) =>
          resolveSection(section, rowsByPath, isExcludedPath, {
            requirePages: value.schema === 1,
            nestingBudget: value.schema === 2 ? 1 : 0,
          })
        );

        return sections.length > 0 ? sections : null;
      } catch {
        return null;
      }
    },

    deriveWikiSections<File extends KbViewFile>(
      rows: File[],
    ): WikiMenuSection<File>[] {
      const wikiRows = rows.flatMap((file) => {
        if (isExcludedPath(file.path)) return [];
        const path = relativeWikiPath(file.path);
        return path ? [{ file, path }] : [];
      });
      if (wikiRows.length === 0) return [];

      const sections: WikiMenuSection<File>[] = [];
      const overview = wikiRows
        .filter(({ path }) => path === "README.md")
        .map(({ file }) => ({ file, label: fileLabel(file) }));
      if (overview.length > 0) {
        sections.push({ label: "Overview", items: overview });
      }

      for (const [prefix, label] of WIKI_SECTIONS) {
        const items = wikiRows
          .filter(({ path }) => path.startsWith(`${prefix}/`))
          .map(({ file }) => ({ file, label: fileLabel(file) }));
        if (items.length > 0) sections.push({ label, items });
      }

      // LAT-1905 — every remaining folder, so a Knowledge Base whose shape is
      // not the CodeWiki neighborhood still gets a complete menu. Known
      // prefixes keep their authored order above; these follow alphabetically
      // so the menu is stable across catalog orderings. Without this pass the
      // folders were dropped outright rather than mis-ordered, which is why an
      // organisation Knowledge Base lit up only Decisions.
      const remainingFolders = [
        ...new Set(
          wikiRows.flatMap(({ path }) => {
            const slash = path.indexOf("/");
            if (slash <= 0) return [];
            const folder = path.slice(0, slash);
            if (KNOWN_SECTION_PREFIXES.has(folder)) return [];
            if (DERIVED_SPECIAL_FOLDERS.has(folder)) return [];
            return [folder];
          }),
        ),
      ].sort();

      for (const folder of remainingFolders) {
        const items = wikiRows
          .filter(({ path }) => path.startsWith(`${folder}/`))
          .map(({ file }) => ({ file, label: fileLabel(file) }));
        if (items.length > 0) {
          sections.push({ label: derivedFolderLabel(folder), items });
        }
      }

      const coverage = wikiRows
        .filter(({ path }) => path === "meta/coverage-map.md")
        .map(({ file }) => ({ file, label: fileLabel(file) }));
      if (coverage.length > 0) {
        sections.push({ label: "Coverage map", items: coverage });
      }

      return sections;
    },
  };
}
